import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Film } from "lucide-react";
import { EmptyState, PageHeader, Spinner, TitleGrid } from "@/components/catalog/TitleGrid";
import { useProfiles } from "@/context/ProfileContext";
import { api } from "@/lib/api";

const KINDS = {
  originals: { eyebrow: "AETRYX Originals", title: "Originals", params: { original: true }, blurb: "Every world here was imagined and produced by AETRYX AI Studios." },
  movies: { eyebrow: "Feature films", title: "Movies", params: { type: "movie" }, blurb: "Big-screen originals in HD with 5.1 sound." },
  series: { eyebrow: "Binge-worthy", title: "TV Series", params: { type: "series" }, blurb: "Multi-season sagas with new chapters every season." },
  shorts: { eyebrow: "Micro-cinema", title: "Shorts", params: { type: "short" }, blurb: "Stories that hit hard in a few minutes." },
};

export default function Browse({ kind }) {
  const cfg = KINDS[kind];
  const { activeProfile } = useProfiles();
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const [genres, setGenres] = useState([]);
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/catalog/genres").then((r) => setGenres(r.data)).catch(() => {}); }, [activeProfile?.id]);
  useEffect(() => {
    setItems(null);
    api.get("/catalog/titles", { params: { ...cfg.params, ...(genre ? { genre } : {}) } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [kind, genre, activeProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid={`browse-page-${kind}`}>
      <PageHeader eyebrow={cfg.eyebrow} title={cfg.title}>
        <p className="mt-4 max-w-xl text-sm sm:text-base text-[#8B8B8B]">{cfg.blurb}</p>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="genre-filter">
          {["", ...genres].map((g) => (
            <button
              key={g || "all"}
              data-testid={`genre-chip-${(g || "all").toLowerCase()}`}
              onClick={() => setParams(g ? { genre: g } : {})}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${genre === g ? "bg-gradient-to-r from-[#F5C451] to-[#D4AF37] text-black" : "border border-[#D4AF37]/30 text-white/80 hover:border-[#FFD86A] hover:text-[#FFD86A]"}`}
            >
              {g || "All"}
            </button>
          ))}
        </div>
      </PageHeader>
      <div className="mx-auto max-w-[1680px] px-4 sm:px-6 lg:px-10">
        {!items ? <Spinner /> : items.length ? <TitleGrid items={items} testId="browse-grid" /> : (
          <EmptyState icon={Film} title="Nothing here yet" text="No titles match this filter on this profile." testId="browse-empty" />
        )}
      </div>
    </div>
  );
}
